import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { resetPassword } from '../api/services'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'

const extractError = (err) => {
    const detail = err?.response?.data?.detail
    const status = err?.response?.status

    if (status === 400) {
        return typeof detail === 'string'
            ? detail
            : 'This reset link is invalid or has expired. Please request a new one.'
    }

    if (typeof detail === 'string') return detail
    if (!err?.response) return 'Unable to reach the server. Please check your connection.'

    return 'Password reset failed. Please try again.'
}

export default function ResetPassword() {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const token = searchParams.get('token')

    const [password, setPassword] = useState('')
    const [confirm, setConfirm] = useState('')
    const [error, setError] = useState('')
    const [success, setSuccess] = useState(false)
    const [loading, setLoading] = useState(false)

    const isMounted = useRef(true)
    useEffect(() => {
        isMounted.current = true
        return () => { isMounted.current = false }
    }, [])

    // No token in URL — show error immediately, don't render the form
    if (!token) {
        return (
            <div className="min-h-screen bg-brand-navy flex flex-col">
                <Navbar />
                <div className="flex-1 flex items-center justify-center px-4 sm:px-6 py-12">
                    <div className="w-full max-w-md">
                        <h1 className="font-display text-3xl font-bold text-brand-text mb-4">
                            Invalid reset link
                        </h1>
                        <p className="text-brand-muted font-body text-sm mb-6">
                            This password reset link is missing a verification token.
                            Please request a new one.
                        </p>
                        <Link to="/forgot-password" className="btn-primary block text-center">
                            Request new link
                        </Link>
                    </div>
                </div>
                <Footer />
            </div>
        )
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setError('')

        if (password !== confirm) {
            setError('Passwords do not match.')
            return
        }

        setLoading(true)

        try {
            await resetPassword({ token, password })
            if (isMounted.current) setSuccess(true)
            // Redirect to login after 2 seconds so user sees the success message
            setTimeout(() => {
                if (isMounted.current) navigate('/login')
            }, 2000)
        } catch (err) {
            if (isMounted.current) setError(extractError(err))
        } finally {
            if (isMounted.current) setLoading(false)
        }
    }

    if (success) {
        return (
            <div className="min-h-screen bg-brand-navy flex flex-col">
                <Navbar />
                <div className="flex-1 flex items-center justify-center px-4 sm:px-6 py-12">
                    <div className="w-full max-w-md text-center">
                        <h1 className="font-display text-3xl font-bold text-brand-text mb-4">
                            Password updated
                        </h1>
                        <p className="text-brand-muted font-body text-sm">
                            Your password has been updated. Redirecting you to sign in…
                        </p>
                    </div>
                </div>
                <Footer />
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-brand-navy flex flex-col">
            <Navbar />
            <div className="flex-1 flex items-center justify-center px-4 sm:px-6 py-12">
                <div className="w-full max-w-md">

                    <div className="mb-8">
                        <h1 className="font-display text-3xl font-bold text-brand-text mb-2">
                            Set new password
                        </h1>
                        <p className="text-brand-muted font-body text-sm">
                            Choose a strong password for your account.
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div>
                            <label className="label">New password</label>
                            <input
                                name="password"
                                type="password"
                                placeholder="Minimum 8 characters"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="input-field"
                                required
                                minLength={8}
                                disabled={loading}
                                autoComplete="new-password"
                            />
                        </div>

                        <div>
                            <label className="label">Confirm new password</label>
                            <input
                                name="confirm"
                                type="password"
                                placeholder="Repeat your new password"
                                value={confirm}
                                onChange={(e) => setConfirm(e.target.value)}
                                className="input-field"
                                required
                                minLength={8}
                                disabled={loading}
                                autoComplete="new-password"
                            />
                        </div>

                        {error && (
                            <div className="border border-red-500/30 bg-red-500/10 rounded px-4 py-3">
                                <p className="text-red-400 text-sm font-body">{error}</p>
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            className="btn-primary w-full text-center disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Updating password…' : 'Update password'}
                        </button>
                    </form>

                    <div className="mt-6 text-center">
                        <Link
                            to="/login"
                            className="text-xs font-body text-brand-muted hover:text-brand-amber transition-colors"
                        >
                            Back to sign in
                        </Link>
                    </div>

                </div>
            </div>
            <Footer />
        </div>
    )
}